<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="iV Challenges" backTo="tools" />

    <main class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md relative">
        <div class="flex flex-col mb-4 space-y-4">
          <div class="w-full flex flex-wrap lg:flex-nowrap items-center justify-between gap-y-2">
            <div class="flex items-center space-x-2">
              <div class="flex items-center space-x-1">
                <span class="text-sm font-semibold">Level:</span>
                <select 
                  id="accessLevel" 
                  name="accessLevel" 
                  class="h-8 border border-blue-400 focus:border-blue-500 rounded pl-3 pr-8 text-sm bg-white text-gray-700 outline-none"
                  v-model="accessLevel" 
                  @change="onAccessLevelChanged">
                  <option value="all">All</option>
                  <option value="service">Service</option>
                  <option value="reserved">Reserved</option>
                  <option value="chargeable">Chargeable</option>
                </select>
              </div>
              <!-- Date Filters -->
              <div class="flex items-center space-x-1">
                <span class="text-sm font-semibold">From:</span>
                <Datepicker 
                  v-model="fromDate" 
                  input-class="h-8 border border-blue-400 focus:border-blue-500 rounded px-3 w-32 text-sm outline-none" 
                  calendar-class="left-0"
                  @input="onDateChanged" 
                  format="dd/MM/yyyy" 
                  placeholder="From Date">
                </Datepicker>
              </div>
              <div class="flex items-center space-x-1">
                <span class="text-sm font-semibold">To:</span>
                <Datepicker 
                  v-model="toDate" 
                  input-class="h-8 border border-blue-400 focus:border-blue-500 rounded px-3 w-32 text-sm outline-none"
                  calendar-class="left-0" 
                  @input="onDateChanged" 
                  format="dd/MM/yyyy" 
                  placeholder="To Date">
                </Datepicker>
              </div>
              <!-- Date Preset Dropdown -->
              <select 
                id="datePreset" 
                name="datePreset" 
                class="h-8 border border-blue-400 focus:border-blue-500 rounded px-2 text-sm bg-white text-gray-700 outline-none"
                v-model="datePreset" 
                @change="applyDatePreset">
                <option value="">Custom Date Range</option>
                <option value="mtd">This Month</option>
                <option value="lcm">Last Month</option>
                <option value="lq">Last Quarter</option>
                <option value="ytd">Year to Date</option>
                <option value="ly">Last Year</option>
              </select>
              
              <div class="flex items-center">
                <input
                  type="text"
                  class="w-48 lg:w-64 rounded-l bge-input bge-input-spacing h-8 border-r-0"
                  placeholder="Search"
                  v-model="searchTerm"
                  @keydown.enter="searchChallengeCodes" />
                <button
                  @click="searchChallengeCodes"
                  class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 h-8 px-3 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out flex items-center justify-center">
                  <svg
                    class="stroke-current h-4 w-4"
                    fill="none"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                  </svg>
                </button>
              </div>

              <button 
                :disabled="accessLevel === 'all' && !fromDate && !toDate && !searchTerm && !searchTermWas" 
                @click="clearFilters" 
                title="Clear filters"
                class="h-8 flex flex-col justify-center px-4 border rounded bg-transparent text-sm font-semibold transition-colors"
                :class="(accessLevel === 'all' && !fromDate && !toDate && !searchTerm && !searchTermWas) ? 'opacity-80 border-gray-400 text-gray-400 cursor-default' : 'border-gray-600 text-gray-700 hover:bg-gray-100 cursor-pointer'">
                <i class="pi pi-filter-slash"></i>
              </button>
            </div>
            
            <div class="flex items-center">
              <button 
                @click="exportCsv"
                :disabled="isExporting" 
                class="h-8 flex items-center px-4 border rounded bg-transparent text-sm font-semibold transition-colors"
                :class="isExporting ? 'opacity-80 border-gray-400 text-gray-400 cursor-default' : 'border-gray-600 text-gray-700 hover:bg-gray-100 cursor-pointer'">
                <i v-if="!isExporting" class="pi pi-file-excel mr-2"></i>
                <spinner v-else class="mr-2" :color="'text-gray-700'" :size="4" />
                Export CSV
              </button>
            </div>
          </div>
        </div>

        <div v-if="viewState == 'Idle'">
          <Table
            :columns="columns"
            :data="challengeCodes"
            :disableHover="true"
            :totalRecords="paginationOptions.totalRecords"
            :hasPagination="true"
            :currentPage="paginationOptions.currentPage"
            :totalPages="paginationOptions.totalPages"
            :initialSortOptions="sortOptions"
            @onSortChanged="onTableSortChanged"
            @onPageChanged="onTablePageChanged">
            <template v-slot:agent="props">
              <p>{{ props.row.data && props.row.data.local_user ? props.row.data.local_user.org_name : '-' }}</p>
            </template>
            <template v-slot:client="props">
              <p>{{ props.row.data && props.row.data.client ? props.row.data.client.org_name : '-' }}</p>
            </template>
            <template v-slot:user="props">
              <p>{{ props.row.data && props.row.data.local_user ? `${props.row.data.local_user.firstname} ${props.row.data.local_user.lastname}` : '-' }}</p>
            </template>
            <template v-slot:code_level="props">
              <p>{{ props.row.data && props.row.data.access_request ? capitalizeFirstLetter(props.row.data.access_request.access_level) : '-' }}</p>
            </template>
            <template v-slot:intent_description="props">
              <p>{{ props.row.data && props.row.data.access_request ? formatIntent(props.row.data.access_request.intent_description) : '-' }}</p>
            </template>
            <template v-slot:created_at="props">
              <p>{{ formatDate(props.row.created_at) }}</p>
            </template>
          </Table>
        </div>
        <div v-show="viewState == 'Loading'">
          <spinner class="mx-auto my-4" :color="'text-black'" :size="8" />
        </div>

        <div v-if="error">
          <div class="bg-red-200 border border-red-500 rounded-lg py-4 px-5 text-red-900">
            <p>Error loading challenge codes {{ error ? `(${error})` : `` }}</p>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Table from "@/components/Table.vue";
import Spinner from "@/components/Spinner.vue";
import moment from "moment";
import Datepicker from "vuejs-datepicker";

export default {
  name: "IvyChallengeCodes",
  components: {
    PageHeader,
    Table,
    Spinner,
    Datepicker,
  },
  data() {
    return {
      searchTerm: "",
      searchTermWas: "",
      datePreset: "",
      fromDate: null,
      toDate: null,
      accessLevel: "all",
      isExporting: false,
      viewState: "Idle",
      challengeCodes: [],
      error: null,
      columns: [
        { title: "Agent", field: "agent", type: "custom" },
        { title: "Customer", field: "client", type: "custom" },
        { title: "Engineer", field: "user", type: "custom" },
        { title: "Code Level", field: "code_level", type: "custom" },
        { title: "Intent", field: "intent_description", type: "custom" },
        { title: "Date/Time", field: "created_at", type: "custom" },
      ],
      paginationOptions: {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
      },
      sortOptions: {
        field: "created_at",
        isAsc: false,
        pageSize: 100,
      },
    };
  },
  async mounted() {
    await this.getChallengeCodes();
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) {
        return "";
      }

      const date = timestamp.toString().length === 10 ? moment.unix(timestamp) : moment(timestamp);
      return date.format("DD/MM/YYYY HH:mm:ss");
    },
    formatIntent(intent) {      
      // Split CamelCase strings with spaces (e.g. "GasCalibration" -> "Gas Calibration")
      return intent 
        ? intent.replace(/([A-Z])/g, ' $1').trim() 
        : "";
    },
    capitalizeFirstLetter(str) {
      return str 
        ? str.charAt(0).toUpperCase() + str.slice(1)
        : "";
    },
    onAccessLevelChanged() {
      this.paginationOptions.currentPage = 1;
      this.getChallengeCodes();
    },
    onDateChanged() {
      this.datePreset = "";
      this.paginationOptions.currentPage = 1;
      this.getChallengeCodes();
    },
    applyDatePreset() {
      const today = moment();
      
      switch (this.datePreset) {
        case "mtd":
          this.fromDate = today.clone().startOf('month').toDate();
          this.toDate = today.toDate();
          break;

        case "lcm":
          this.fromDate = today.clone().subtract(1, 'months').startOf('month').toDate();
          this.toDate = today.clone().subtract(1, 'months').endOf('month').toDate();
          break;

        case "lq":
          this.fromDate = today.clone().subtract(3, 'months').startOf('month').toDate();
          this.toDate = today.clone().subtract(1, 'months').endOf('month').toDate();
          break;

        case "ytd":
          this.fromDate = today.clone().startOf('year').toDate();
          this.toDate = today.toDate();
          break;

        case "ly":
          this.fromDate = today.clone().subtract(1, 'years').startOf('year').toDate();
          this.toDate = today.clone().subtract(1, 'years').endOf('year').toDate();
          break;

        case "":
        default:
          this.fromDate = null;
          this.toDate = null;
          break;
      }
      
      this.paginationOptions.currentPage = 1;
      this.getChallengeCodes();
    },
    clearFilters() {
      this.accessLevel = "all";
      this.datePreset = "";
      this.fromDate = null;
      this.toDate = null;
      this.searchTerm = "";
      this.searchTermWas = "";
      this.paginationOptions.currentPage = 1;
      this.getChallengeCodes();
    },
    searchChallengeCodes() {
      if (!this.searchTerm || this.searchTerm.trim().length === 0) {
        this.searchTermWas = "";
      } 
      else {
        this.searchTermWas = this.searchTerm.trim();
      }
      
      this.paginationOptions.currentPage = 1;
      this.getChallengeCodes();
    },
    async exportCsv() {
      if (this.isExporting) { 
        return;
      }

      try {
        this.isExporting = true;
        const queryOpts = {};

        if (this.searchTermWas) {
          queryOpts.searchTerm = this.searchTermWas;
        } 

        if (this.accessLevel !== "all") {
          queryOpts.accessLevel = this.accessLevel;
        }

        if (this.fromDate) {
          queryOpts.fromDate = moment(this.fromDate).format('YYYY-MM-DD');
        }
        if (this.toDate) {
          queryOpts.toDate = moment(this.toDate).format('YYYY-MM-DD');
        }

        const blob = await this.iVClientService.exportChallengeOverridesCsv(queryOpts);
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'challenge-codes.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } 
      catch (error) {
        console.error('Error exporting CSV', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Export Failed',
          detail: 'There was a problem exporting the CSV.',
          life: 3000
        });
      } 
      finally {
        this.isExporting = false;
      }
    },
    async getChallengeCodes() {
      this.viewState = "Loading";
      this.error = null;

      try {
        const queryOpts = {
          ...this.paginationOptions,
          ...this.sortOptions,
        };
        
        const fieldMap = {
          agent: "data.local_user.org_name",
          client: "data.client.org_name",
          user: "data.local_user.firstname",
          code_level: "data.access_request.access_level",
          intent_description: "data.access_request.intent_description",
          created_at: "created_at"
        };

        if (queryOpts.field && fieldMap[queryOpts.field]) {
          queryOpts.field = fieldMap[queryOpts.field];
        }

        if (this.searchTermWas) {
          queryOpts.searchTerm = this.searchTermWas;
        }

        if (this.accessLevel !== "all") {
          queryOpts.accessLevel = this.accessLevel;
        }

        if (this.fromDate) {
          queryOpts.fromDate = moment(this.fromDate).format('YYYY-MM-DD');
        }

        if (this.toDate) {
          queryOpts.toDate = moment(this.toDate).format('YYYY-MM-DD');
        }

        let result = await this.iVClientService.getChallengeOverrides(queryOpts);
        
        this.challengeCodes = result.data;
        this.paginationOptions.totalRecords = result.count;
        this.paginationOptions.totalPages = result.page_count;
        this.viewState = "Idle";
      } 
      catch (error) {
        console.error('Error getting challenge codes', error);
        this.viewState = "Error";
        this.error = error;
      }
    },
    async onTablePageChanged(direction) {
      if (direction === "next" || direction === 1) {
        this.paginationOptions.currentPage++;
      } 
      else if (direction === "previous" || direction === -1) {
        this.paginationOptions.currentPage--;
      }

      await this.getChallengeCodes();
    },
    async onTableSortChanged(options) {
      this.sortOptions = options;
      this.paginationOptions.currentPage = 1;
      await this.getChallengeCodes();
    },
  },
};
</script>